import React from 'react';
import {
  GROWTH_APPROVAL_BREAKDOWN,
  GROWTH_AUTOPILOT_ACTIONS,
  GROWTH_BRAIN_INSIGHTS,
  GROWTH_BUSINESS_METRICS,
  GROWTH_DASHBOARD_META,
  GROWTH_QUICK_NAV,
  GROWTH_VALUE_CARDS,
} from '../../growth-dashboard';
import type {
  GrowthAutopilotAction,
  GrowthBrainInsight,
  GrowthMetricDef,
  GrowthQuickNavItem,
  GrowthValueCardDef,
} from '../../growth-dashboard/types';
import { Icon } from '../Icon/Icon';
import { GdCard, GdMetric, GdSectionHead, GdValueCard, GROWTH_TONE } from './GrowthDashboardParts';
import { GrowthIcon } from './GrowthIcon';
import './growth-dashboard.css';

export interface GrowthDashboardHighlight {
  name: string;
  subtitle: string;
  stats: readonly [string, string, string][];
}

export interface GrowthDashboardData {
  period?: string;
  updated?: string;
  autopilotActive?: boolean;
  approvalsTotal?: number;
  approvalBreakdown?: readonly (readonly [string, string])[];
  businessMetrics?: GrowthMetricDef[];
  brainInsightCount?: number;
  brainInsights?: GrowthBrainInsight[];
  valueCards?: GrowthValueCardDef[];
  topCampaign?: GrowthDashboardHighlight;
  biggestRankingGain?: GrowthDashboardHighlight;
  autopilotActions?: GrowthAutopilotAction[];
  quickNav?: GrowthQuickNavItem[];
}

export interface GrowthDashboardViewProps {
  showAmbientGlow?: boolean;
  /** Real backend payload — when set, replaces internal mock sections. */
  data?: GrowthDashboardData;
  loading?: boolean;
  /** Called when user clicks "View full log" — if omitted, renders plain link. */
  onAutopilotLogClick?: () => void;
}

export function GrowthDashboardView({
  showAmbientGlow = true,
  data,
  loading = false,
  onAutopilotLogClick,
}: GrowthDashboardViewProps) {
  const meta = GROWTH_DASHBOARD_META;
  const connected = data !== undefined;

  const period = data?.period ?? meta.period;
  const updated = data?.updated ?? meta.updated;
  const autopilotActive = connected ? data.autopilotActive === true : true;
  const approvalsTotal = connected ? (data.approvalsTotal ?? 0) : 7;
  const approvalBreakdown = connected ? (data.approvalBreakdown ?? []) : GROWTH_APPROVAL_BREAKDOWN;
  const businessMetrics = connected ? (data.businessMetrics ?? []) : GROWTH_BUSINESS_METRICS;
  const brainInsightCount = connected ? (data.brainInsightCount ?? 0) : 19;
  const brainInsights = connected ? (data.brainInsights ?? []) : GROWTH_BRAIN_INSIGHTS;
  const valueCards = connected ? (data.valueCards ?? []) : GROWTH_VALUE_CARDS;
  const autopilotActions = connected ? (data.autopilotActions ?? []) : GROWTH_AUTOPILOT_ACTIONS;
  const quickNav = data?.quickNav ?? GROWTH_QUICK_NAV;
  const topCampaign = connected
    ? data.topCampaign
    : {
        name: 'Dental Implants NYC',
        subtitle: '12 bookings · $18,400 est. revenue · $89 CPA',
        stats: [
          ['CTR', '7.2%', 'var(--success)'],
          ['Conversion', '14%', 'var(--success)'],
          ['Spend', '$1,068', 'var(--ink-0)'],
        ] as const,
      };
  const biggestRankingGain = connected
    ? data.biggestRankingGain
    : {
        name: '“Emergency Dentist NYC”',
        subtitle: 'Page 2 (#14) → Page 1 (#6) in 28 days',
        stats: [
          ['Monthly searches', '3,200', 'var(--ink-0)'],
          ['Est. traffic gain', '+180/mo', 'var(--success)'],
        ] as const,
      };
  const [moneySaved, errorsCaught, ...bottomValueCards] = valueCards;
  const showApprovals = !connected || approvalsTotal > 0;
  const showHighlights = Boolean(topCampaign || biggestRankingGain);
  const metricsColumns = Math.max(businessMetrics.length, 1);

  return (
    <div className="growth-dashboard-root relative mx-auto w-full max-w-[1120px] px-[30px] pb-[90px] pt-6">
      {showAmbientGlow ? (
        <div
          className="elva-glow pointer-events-none absolute -right-[140px] -top-[240px] h-[560px] w-[560px]"
          aria-hidden
        />
      ) : null}

      <div className="relative z-[1]">
        <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Icon kind="target" size={13} stroke={2} className="text-[var(--ai-3)]" />
              <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
                {meta.eyebrow}
              </span>
            </div>
            <h1 className="m-0 font-[var(--font-display)] text-[clamp(28px,3.2vw,36px)] font-semibold leading-[1.1] tracking-[-0.035em] text-[var(--ink-0)]">
              {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
            </h1>
            <p className="mt-2 text-[13.5px] text-[var(--ink-3)]">
              {period} · <span className="text-[var(--ink-2)]">{updated}</span>
              {loading ? <span className="ml-2 text-[var(--ink-4)]">· Updating…</span> : null}
            </p>
          </div>
          {autopilotActive ? (
            <span className="inline-flex items-center gap-2 rounded-[var(--r-pill)] border border-[color-mix(in_srgb,var(--success)_24%,transparent)] bg-[var(--success-tint)] px-3.5 py-[7px]">
              <span className="elva-live h-2 w-2" />
              <span className="text-[12.5px] font-semibold text-[var(--success)]">
                Autopilot active
              </span>
            </span>
          ) : null}
        </div>

        {showApprovals ? (
          <div className="elva-grad-ink relative mb-8 flex flex-wrap items-center justify-between gap-5 overflow-hidden rounded-[var(--r-xl)] px-[30px] py-6 shadow-[var(--sh-2)]">
            <div
              className="elva-grad-spectrum-fill pointer-events-none absolute -right-[120px] -top-20 h-60 w-60 rounded-full opacity-35 blur-[70px]"
              aria-hidden
            />
            <div className="relative flex flex-wrap items-center gap-[22px]">
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-[15px] border border-white/15 bg-white/10 text-white">
                <Icon kind="check-circ" size={27} stroke={1.9} />
              </span>
              <div>
                <div className="mb-[7px] text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/60">
                  Needs your approval
                </div>
                <div className="mb-2 font-[var(--font-display)] text-[26px] font-semibold tracking-[-0.02em] text-white">
                  {approvalsTotal} items waiting for you
                </div>
                <div className="flex flex-wrap gap-3 text-[13px] text-white/70">
                  {approvalBreakdown.map(([count, label], index) => (
                    <React.Fragment key={label}>
                      {index > 0 ? <span className="text-white/30">·</span> : null}
                      <span>
                        <b className="font-bold text-white">{count}</b> {label}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="relative inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 bg-[var(--cream-1)] px-[26px] py-[13px] font-[var(--font-body)] text-sm font-semibold text-[var(--ink-0)] shadow-[var(--sh-btn)]"
            >
              Review now <Icon kind="arrow-r" size={15} stroke={2.2} />
            </button>
          </div>
        ) : null}

        <GdSectionHead title="Business performance" sub={period} />
        <div
          className="growth-metrics-grid mb-9 grid gap-3"
          style={{ gridTemplateColumns: `repeat(${metricsColumns}, minmax(0, 1fr))` }}
        >
          {businessMetrics.map((metric) => (
            <GdMetric key={metric.label} {...metric} />
          ))}
        </div>

        <GdSectionHead
          icon="sparkle"
          accent="ai"
          title="What ELVA did for you this month"
          sub="AI decisions powered by your real PMS and market data — working 24/7"
        />
        <div
          className="growth-ai-grid mb-3.5 grid gap-3.5"
          style={{ gridTemplateColumns: '2fr 1fr 1fr' }}
        >
          <div className="elva-grad-ink relative overflow-hidden rounded-[var(--r-xl)] p-[22px_26px] text-white shadow-[var(--sh-2)]">
            <div
              className="elva-grad-spectrum-fill pointer-events-none absolute -right-10 -top-[70px] h-[200px] w-[200px] rounded-full opacity-40 blur-[60px]"
              aria-hidden
            />
            <div className="relative">
              <div className="mb-3 flex items-center gap-2">
                <Icon kind="sparkle" size={15} stroke={2} className="text-white" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-white/80">
                  AI Brain insight
                </span>
              </div>
              <div className="mb-1.5 font-[var(--font-display)] text-[26px] font-semibold tracking-[-0.03em]">
                {brainInsightCount} real-time adjustments
              </div>
              <div className="mb-5 text-[13.5px] leading-normal text-white/75">
                Decisions based on your live PMS schedule and patient data.
              </div>
              <div className="grid grid-cols-2 gap-4">
                {brainInsights.map((item) => (
                  <div key={item.heading}>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-white/55">
                      {item.heading}
                    </div>
                    <div className="text-[12.5px] font-medium leading-normal text-white/95">
                      {item.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {moneySaved && errorsCaught ? (
            <>
              <GdValueCard {...moneySaved} />
              <GdValueCard {...errorsCaught} />
            </>
          ) : null}
        </div>
        {bottomValueCards.length > 0 ? (
          <div
            className="growth-value-grid mb-9 grid gap-3.5"
            style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            {bottomValueCards.map((card) => (
              <GdValueCard key={card.label} {...card} />
            ))}
          </div>
        ) : null}

        {showHighlights ? (
          <>
            <GdSectionHead title="This week's highlights" />
            <div
              className="growth-highlights-grid mb-8 grid gap-3.5"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              {topCampaign ? (
                <GdCard>
                  <div className="mb-3.5 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                      Top performing campaign
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-[3px] text-[11px] font-semibold text-[var(--success)]">
                      <Icon kind="up" size={11} stroke={2.6} />
                      Trending up
                    </span>
                  </div>
                  <div className="mb-1 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    {topCampaign.name}
                  </div>
                  <div className="mb-[18px] text-[12.5px] text-[var(--ink-3)]">
                    {topCampaign.subtitle}
                  </div>
                  <div className="flex items-center gap-[22px] border-t border-[var(--cream-2)] pt-[15px]">
                    {topCampaign.stats.map(([label, value, color]) => (
                      <div key={label}>
                        <div className="mb-[3px] text-[10.5px] text-[var(--ink-3)]">{label}</div>
                        <div
                          className="font-[var(--font-display)] text-[17px] font-bold"
                          style={{ color }}
                        >
                          {value}
                        </div>
                      </div>
                    ))}
                    <span className="growth-link ml-auto inline-flex cursor-pointer items-center gap-1 text-[12.5px] font-semibold text-[var(--ai-3)]">
                      View campaign <Icon kind="arrow-r" size={12} stroke={2.2} />
                    </span>
                  </div>
                </GdCard>
              ) : null}
              {biggestRankingGain ? (
                <GdCard>
                  <div className="mb-3.5 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]">
                      Biggest ranking gain
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--info-tint)] px-2.5 py-[3px] text-[11px] font-semibold text-[var(--info)]">
                      SEO win
                    </span>
                  </div>
                  <div className="mb-1 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    {biggestRankingGain.name}
                  </div>
                  <div className="mb-[18px] text-[12.5px] text-[var(--ink-3)]">
                    {biggestRankingGain.subtitle}
                  </div>
                  <div className="flex items-center gap-[22px] border-t border-[var(--cream-2)] pt-[15px]">
                    {biggestRankingGain.stats.map(([label, value, color]) => (
                      <div key={label}>
                        <div className="mb-[3px] text-[10.5px] text-[var(--ink-3)]">{label}</div>
                        <div
                          className="font-[var(--font-display)] text-[17px] font-bold"
                          style={{ color }}
                        >
                          {value}
                        </div>
                      </div>
                    ))}
                    <span className="growth-link ml-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-[var(--ai-3)]">
                      View rankings <Icon kind="arrow-r" size={12} stroke={2.2} />
                    </span>
                  </div>
                </GdCard>
              ) : null}
            </div>
          </>
        ) : null}

        <GdCard className="mb-8 overflow-hidden !p-0">
          <div className="relative flex items-center gap-3.5 border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-[15px] pl-[22px]">
            <span
              className="absolute bottom-0 left-0 top-0 w-[3px]"
              style={{
                background: 'linear-gradient(180deg, var(--ai-3), var(--ai-4) 58%, var(--ai-5))',
              }}
            />
            <div className="flex-1">
              <h3 className="m-0 font-[var(--font-display)] text-[15.5px] font-bold tracking-[-0.02em] text-[var(--ink-0)]">
                Latest autopilot actions
              </h3>
              <p className="m-0 mt-0.5 text-[11.5px] text-[var(--ink-3)]">
                Decisions ELVA made for you in the last 24 hours
              </p>
            </div>
            <a
              href="/autopilot-log"
              onClick={
                onAutopilotLogClick
                  ? (e) => {
                      e.preventDefault();
                      onAutopilotLogClick();
                    }
                  : undefined
              }
              className="growth-link inline-flex cursor-pointer items-center gap-1 text-[12.5px] font-semibold text-[var(--ai-3)]"
            >
              View full log <Icon kind="arrow-r" size={12} stroke={2.2} />
            </a>
          </div>
          <div>
            {autopilotActions.length > 0 ? (
              autopilotActions.map((action, index) => {
                const tone = GROWTH_TONE[action.tone];
                return (
                  <div
                    key={`${action.time}-${action.action}`}
                    className="growth-act-row flex items-center gap-[15px] border-[var(--cream-2)] px-5 py-[13px] transition-[background] duration-[var(--dur-fast)]"
                    style={{
                      borderBottom:
                        index < autopilotActions.length - 1
                          ? '1px solid var(--cream-2)'
                          : undefined,
                    }}
                  >
                    <span
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]"
                      style={{ background: tone.tint, color: tone.accent }}
                    >
                      <GrowthIcon kind={action.icon} size={15} stroke={1.9} />
                    </span>
                    <span className="min-w-[72px] font-[var(--font-mono)] text-[11.5px] text-[var(--ink-4)]">
                      {action.time}
                    </span>
                    <span className="min-w-[84px]">
                      <span
                        className="rounded-[var(--r-sm)] px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.05em]"
                        style={{ color: tone.accent, background: tone.tint }}
                      >
                        {action.cat}
                      </span>
                    </span>
                    <span className="flex-1 text-[13px] leading-[1.45] text-[var(--ink-1)]">
                      {action.action}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="px-5 py-8 text-center text-[13px] text-[var(--ink-3)]">
                No recent autopilot actions.
              </div>
            )}
          </div>
        </GdCard>

        <div
          className="growth-quick-nav-grid grid gap-3"
          style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
        >
          {quickNav.map((item) => {
            const tone = GROWTH_TONE[item.tone];
            return (
              <div
                key={item.title}
                className="growth-quick-nav cursor-pointer rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[18px_20px] shadow-[var(--sh-1)] transition-[transform,box-shadow,border-color] duration-[var(--dur-fast)]"
              >
                <span
                  className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-[10px]"
                  style={{ background: tone.tint, color: tone.accent }}
                >
                  <GrowthIcon kind={item.icon} size={18} stroke={1.9} />
                </span>
                <div className="mb-[3px] font-[var(--font-display)] text-base font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
                  {item.title}
                </div>
                <div className="text-xs text-[var(--ink-3)]">{item.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
